import { API, Utils, digitsOnly, resolveSortField } from '@enrich/shared-lib';
import QueryString from 'qs';

// 1000 = the Strapi REST maxLimit (enrich-strapi-cms/config/api.js).
const MAX_PAGE_SIZE = 1000;

const SORT_FIELD_MAP = { lastName: 'lastName' };

// A word made only of phone characters, e.g. '(770)' or '866-6105'.
const PHONE_WORD_PATTERN = /^[\d\s()+.-]+$/;

const TEXT_SEARCH_FIELDS = ['firstName', 'lastName', 'email', 'hometown'];

// Every word must match. One word matches on name, email or location, and
// phone-like words also match the phone digits, so '(770)' finds a stored
// '+17708666105' but 'jdoe1@x.com' does not match every phone with a '1'.
export const buildSearchFilter = (search) => {
    const words = search?.trim().split(/\s+/).filter(Boolean) ?? [];
    if (words.length === 0) return undefined;

    return words.map((word) => {
        const conditions = TEXT_SEARCH_FIELDS.map((field) => ({
            [field]: { $containsi: word },
        }));
        const digits = PHONE_WORD_PATTERN.test(word) ? digitsOnly(word) : '';
        if (digits) conditions.push({ phoneNumber: { $containsi: digits } });

        return { $or: conditions };
    });
};

export const buildContactsQuery = ({
    listId,
    page,
    pageSize,
    sort,
    search,
    hasNotesOnly = false,
    majorIds = [],
}) => {
    const filters = { contact_lists: { id: { $eq: listId } } };

    const searchFilter = buildSearchFilter(search);
    if (searchFilter) filters.$and = searchFilter;

    if (hasNotesOnly) filters.contact_notes = { id: { $notNull: true } };

    // A student matches when they have any one of the selected majors.
    if (majorIds.length > 0) filters.majors = { id: { $in: majorIds } };

    const direction = sort?.direction ?? 'asc';
    const sortField = resolveSortField(sort, SORT_FIELD_MAP, 'lastName');

    return QueryString.stringify(
        {
            filters,
            fields: [
                'firstName',
                'lastName',
                'email',
                'phoneNumber',
                'hometown',
            ],
            populate: {
                majors: { fields: ['name'] },
                contact_notes: { count: true },
            },
            pagination: { page, pageSize },
            sort: [`${sortField}:${direction}`, `firstName:${direction}`],
        },
        { encodeValuesOnly: true },
    );
};

// Returns { contacts, meta } with flat contacts, or null on failure.
export const loadListContacts = async (params) => {
    try {
        const response = await API.get(
            `/api/contacts?${buildContactsQuery(params)}`,
        );
        return {
            contacts: Utils.deepFlatten(response.data.data) ?? [],
            meta: response.data.meta,
        };
    } catch (error) {
        console.log(error);
        return null;
    }
};

// Every major as { id, name }, A to Z, for the filter options.
export const loadMajors = async () => {
    const query = QueryString.stringify(
        {
            fields: ['name'],
            sort: ['name:asc'],
            pagination: { pageSize: MAX_PAGE_SIZE },
        },
        { encodeValuesOnly: true },
    );

    try {
        const response = await API.get(`/api/majors?${query}`);
        return Utils.deepFlatten(response.data.data) ?? [];
    } catch (error) {
        console.log(error);
        return [];
    }
};
